import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	Webhook,
	WebhookDeleteResponse,
	WebhookDeleteResponseSchema,
	WebhookResponse,
	WebhookResponseSchema,
	CreateWebhook,
	UpdateWebhook,
	WebhookCreated,
	WebhookCreatedSchema,
	WebhookSchema,
} from "../../pages/Company/pages/WebhookOverview/schema/webhook";
import {
	WebhookSecretResponse,
	WebhookSecretResponseSchema,
	WebhookTestEventResult,
	WebhookTestEventResultSchema,
} from "../../pages/Company/pages/WebhookOverview/schema/webhookSigning";

import {
	DeliveryQuery,
	RedeliveryQuery,
	RedeliveryCounts,
	RedeliveryCountsSchema,
	WebhookDeliveryPage,
	WebhookDeliveryPageSchema,
	WebhookDeliveryDetail,
	WebhookDeliveryDetailSchema,
} from "../../pages/Company/pages/WebhookOverview/schema/webhookDelivery";

export const webhookApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getDeliveryWebhook: builder.query<Webhook, number>({
			query: (id) => ({ url: `webhook/${id}`, method: "GET", responseSchema: WebhookSchema }),
			providesTags: (_result, _error, id) => ["Webhooks", { type: "Webhook", id }],
		}),
		getWebhookDeliveries: builder.query<WebhookDeliveryPage, DeliveryQuery>({
			query: ({ webhookId, take, ...params }) => ({
				url: `webhook/${webhookId}/deliveries`,
				method: "GET",
				params: { ...params, limit: take },
				responseSchema: WebhookDeliveryPageSchema,
			}),
			transformResponse: (response: unknown) => WebhookDeliveryPageSchema.parse(response),
			providesTags: (_result, _error, { webhookId }) => [
				{ type: "Webhook", id: `deliveries-${webhookId}` },
			],
		}),
		getWebhookDelivery: builder.query<
			WebhookDeliveryDetail,
			{ webhookId: number; deliveryId: number }
		>({
			query: ({ webhookId, deliveryId }) => ({
				url: `webhook/${webhookId}/deliveries/${deliveryId}`,
				method: "GET",
				responseSchema: WebhookDeliveryDetailSchema,
			}),
			transformResponse: (response: unknown) => WebhookDeliveryDetailSchema.parse(response),
			providesTags: (_result, _error, { webhookId }) => [
				{ type: "Webhook", id: `deliveries-${webhookId}` },
			],
		}),
		getWebhookRedeliveryCounts: builder.query<RedeliveryCounts, RedeliveryQuery>({
			query: ({ webhookId, ...params }) => ({
				url: `webhook/${webhookId}/deliveries/redelivery-counts`,
				method: "GET",
				params,
				responseSchema: RedeliveryCountsSchema,
			}),
			transformResponse: (response: unknown) => RedeliveryCountsSchema.parse(response),
			providesTags: (_result, _error, { webhookId }) => [
				{ type: "Webhook", id: `deliveries-${webhookId}` },
			],
		}),
		redeliverWebhook: builder.mutation<RedeliveryCounts, RedeliveryQuery>({
			query: ({ webhookId, ...body }) => ({
				url: `webhook/${webhookId}/deliveries/redeliver`,
				method: "POST",
				body,
				responseSchema: RedeliveryCountsSchema,
			}),
			transformResponse: (response: unknown) => RedeliveryCountsSchema.parse(response),
			invalidatesTags: (_result, error, { webhookId }) =>
				error ? [] : ["Webhooks", { type: "Webhook", id: `deliveries-${webhookId}` }],
		}),
		getWebhooks: builder.query<WebhookResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `/webhook`,
				params: {
					...(params && { ...params }),
				},
				method: "GET",
				responseSchema: WebhookResponseSchema,
			}),
			providesTags: ["Webhooks"],
		}),
		createWebhook: builder.mutation<WebhookCreated, CreateWebhook>({
			query: (data) => ({
				url: `/webhook`,
				method: "POST",
				body: data,
				responseSchema: WebhookCreatedSchema,
			}),
			// A refused save changed nothing; a refetch would only remount the table under the dialog.
			invalidatesTags: (_result, error) => (error ? [] : ["Webhooks"]),
		}),
		updateWebhook: builder.mutation<Webhook, UpdateWebhook>({
			query: (data) => ({
				url: `webhook/${data.id}`,
				method: "PATCH",
				body: data,
				responseSchema: WebhookSchema,
			}),
			invalidatesTags: (_result, error) => (error ? [] : ["Webhooks"]),
		}),
		resumeWebhook: builder.mutation<Webhook, number>({
			query: (id) => ({
				url: `webhook/${id}/resume`,
				method: "POST",
				responseSchema: WebhookSchema,
			}),
			invalidatesTags: ["Webhooks"],
		}),
		// Signing (PREC-4249). Rotate and reveal change the Signing column, so the table refetches;
		// a test event changes nothing on the webhook.
		rotateWebhookSecret: builder.mutation<WebhookSecretResponse, number>({
			query: (id) => ({
				url: `webhook/${id}/rotate-secret`,
				method: "POST",
				responseSchema: WebhookSecretResponseSchema,
			}),
			invalidatesTags: (_result, error) => (error ? [] : ["Webhooks"]),
		}),
		revealWebhookSecret: builder.mutation<WebhookSecretResponse, number>({
			query: (id) => ({
				url: `webhook/${id}/reveal-secret`,
				method: "POST",
				responseSchema: WebhookSecretResponseSchema,
			}),
			invalidatesTags: ["Webhooks"],
		}),
		sendWebhookTestEvent: builder.mutation<WebhookTestEventResult, number>({
			query: (id) => ({
				url: `webhook/${id}/test-event`,
				method: "POST",
				responseSchema: WebhookTestEventResultSchema,
			}),
		}),
		deleteWebhook: builder.mutation<WebhookDeleteResponse, number>({
			query: (id) => ({
				url: `webhook/${id}`,
				method: "DELETE",
				responseSchema: WebhookDeleteResponseSchema,
			}),
			invalidatesTags: ["Webhooks"],
		}),
	}),
});

export const {
	useGetDeliveryWebhookQuery,
	useGetWebhookDeliveriesQuery,
	useGetWebhookDeliveryQuery,
	useGetWebhookRedeliveryCountsQuery,
	useRedeliverWebhookMutation,
	useCreateWebhookMutation,
	useDeleteWebhookMutation,
	useGetWebhooksQuery,
	useResumeWebhookMutation,
	useRevealWebhookSecretMutation,
	useRotateWebhookSecretMutation,
	useSendWebhookTestEventMutation,
	useUpdateWebhookMutation,
} = webhookApi;
export const webhookApiReducer = webhookApi.reducer;
export const webhookApiMiddleware = webhookApi.middleware;
